import { useMemo } from "react";
import { AmpolService, Position } from "types";
import turfDistance from "@turf/distance";

import useAmpolServiceDataStore from "@/use-ampol-service-data-store";
import useRefinementStore from "@/use-refinement-store";

const MAX_DISTANCE = 30;

const useAmpolRefinedServices = () => {
  const ampolServices = useAmpolServiceDataStore(
    (store) => store.services ?? new Map<string, AmpolService>()
  );
  const isLoading = useAmpolServiceDataStore((store) => store.loading);

  const position = useRefinementStore((store) => store.position);
  const services = useRefinementStore((store) => store.services ?? []);
  const bounds = useRefinementStore((store) => store.bounds);

  const refinedAmpolServices = useMemo(() => {
    const results = Array.from(ampolServices.values()).filter(
      (ampolService) =>
        hasServices(ampolService, services) &&
        withinDistance(ampolService, MAX_DISTANCE, bounds)
    );

    // Order by the distance from the center of the bounds
    results.sort((a, b) => {
      if (bounds == null) {
        return 0;
      }

      const centerOfBounds = [
        (bounds[0][0] + bounds[1][0]) / 2,
        (bounds[0][1] + bounds[1][1]) / 2,
      ];

      const distanceA = turfDistance(centerOfBounds, a.position, {
        units: "kilometers",
      });
      const distanceB = turfDistance(centerOfBounds, b.position, {
        units: "kilometers",
      });

      return distanceA - distanceB;
    });

    return results;
  }, [services, ampolServices, bounds]);

  return { ampolServices: refinedAmpolServices, services, isLoading, position };
};

export default useAmpolRefinedServices;

function withinDistance(
  ampolService: AmpolService,
  distance: number,
  bounds: [Position, Position] | null
) {
  if (bounds == null) {
    return false;
  }

  // Get the middle of the bounds
  const centerOfBounds = [
    (bounds[0][0] + bounds[1][0]) / 2,
    (bounds[0][1] + bounds[1][1]) / 2,
  ];

  // Determine the distance from the center of the bounds to the service position
  const distanceFromCenter = turfDistance(
    centerOfBounds,
    ampolService.position,
    {
      units: "kilometers",
    }
  );

  return distanceFromCenter <= distance;
}

function hasServices(ampolService: AmpolService, services: string[]) {
  return services.every((service) => ampolService.services?.includes(service));
}
